import {
  type MaintenanceSchedule,
  type MaintenanceModule,
  moduleMeta,
} from '@/mocks/saGlobalMaintenanceCenter';

interface MaintenanceScheduleListProps {
  schedules: MaintenanceSchedule[];
  onSelect: (schedule: MaintenanceSchedule) => void;
  onDuplicate: (schedule: MaintenanceSchedule) => void;
}

export default function MaintenanceScheduleList({ schedules, onSelect, onDuplicate }: MaintenanceScheduleListProps) {
  const enabled = schedules.filter((s) => s.enabled);
  const disabled = schedules.filter((s) => !s.enabled);

  if (schedules.length === 0) {
    return (
      <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-200 flex items-center justify-center">
          <i className="ri-calendar-todo-line text-foreground-400 text-2xl"></i>
        </div>
        <p className="text-foreground-800 text-sm font-semibold mb-1">Sin programaciones</p>
        <p className="text-foreground-400 text-xs">No hay mantenimientos programados futuros.</p>
      </div>
    );
  }

  function getModulesDisplay(modules: MaintenanceModule[]) {
    if (!modules || modules.length === 0) return null;
    const isAll = modules.includes('all');
    if (isAll) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-background-200 text-foreground-500 text-[9px] font-semibold">
          <i className="ri-global-line text-[8px]"></i>
          Todos
        </span>
      );
    }
    return (
      <div className="flex items-center gap-0.5">
        {modules.map((md) => {
          const meta = moduleMeta[md];
          if (!meta) return null;
          return (
            <span key={md} className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-background-200" title={meta.label}>
              <i className={`${meta.icon} text-foreground-500 text-[9px]`}></i>
            </span>
          );
        })}
      </div>
    );
  }

  function getTypeBadge(type: string) {
    if (type === 'global') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-violet-50 text-violet-700 text-[9px] font-semibold">
          <i className="ri-global-line text-[8px]"></i>
          Global
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-sky-50 text-sky-700 text-[9px] font-semibold">
        <i className="ri-stack-line text-[8px]"></i>
        Módulo
      </span>
    );
  }

  return (
    <div className="space-y-4">
      {/* Enabled schedules */}
      {enabled.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {enabled.map((sch) => (
            <div
              key={sch.id}
              className="bg-background-50 border border-background-200 rounded-xl p-4 hover:border-background-300 transition-colors"
            >
              {/* Header */}
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  {getTypeBadge(sch.type)}
                  {sch.recurring && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-600 text-[9px] font-semibold">
                      <i className="ri-refresh-line text-[8px]"></i>
                      Recurrente
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => onSelect(sch)}
                    className="w-6 h-6 rounded-lg bg-background-200 flex items-center justify-center text-foreground-500 hover:bg-foreground-950 hover:text-background-50 transition-colors cursor-pointer"
                    title="Ver detalles"
                  >
                    <i className="ri-eye-line text-[10px]"></i>
                  </button>
                  <button
                    onClick={() => onDuplicate(sch)}
                    className="w-6 h-6 rounded-lg bg-background-200 flex items-center justify-center text-foreground-500 hover:bg-foreground-950 hover:text-background-50 transition-colors cursor-pointer"
                    title="Duplicar"
                  >
                    <i className="ri-file-copy-line text-[10px]"></i>
                  </button>
                </div>
              </div>

              {/* Name */}
              <p className="text-foreground-800 text-xs font-bold mb-2 line-clamp-2">{sch.name}</p>

              {/* Date */}
              <div className="flex items-center gap-2 mb-2 p-2 rounded-lg bg-background-100">
                <div className="w-7 h-7 rounded-lg bg-foreground-950 flex items-center justify-center shrink-0">
                  <i className="ri-calendar-line text-white text-[13px]"></i>
                </div>
                <div>
                  <p className="text-foreground-700 text-[10px] font-semibold">
                    {new Date(sch.scheduledDate).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                  <p className="text-foreground-500 text-[9px]">
                    {new Date(sch.scheduledDate).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })} UTC · {sch.estimatedDuration}
                  </p>
                </div>
              </div>

              {/* Modules */}
              <div className="flex items-center gap-2 mb-2">
                <span className="text-foreground-400 text-[9px]">Módulos:</span>
                {getModulesDisplay(sch.modules)}
              </div>

              {/* Description */}
              <p className="text-foreground-400 text-[10px] line-clamp-2 leading-relaxed">{sch.description}</p>

              {/* Affected services */}
              {sch.affectedServices.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-background-200">
                  {sch.affectedServices.map((svc) => (
                    <span key={svc} className="px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-600 text-[8px] font-medium">
                      {svc}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Disabled schedules */}
      {disabled.length > 0 && (
        <div>
          <p className="text-foreground-400 text-[10px] font-semibold mb-2 flex items-center gap-1.5">
            <i className="ri-forbid-line text-[10px]"></i>
            Deshabilitadas
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {disabled.map((sch) => (
              <div
                key={sch.id}
                className="bg-background-50 border border-background-200 rounded-xl p-4 opacity-50 hover:opacity-75 transition-opacity"
              >
                <div className="flex items-center gap-2 mb-1">
                  {getTypeBadge(sch.type)}
                </div>
                <p className="text-foreground-800 text-xs font-semibold line-clamp-1 mb-1">{sch.name}</p>
                <p className="text-foreground-400 text-[10px] line-clamp-2">{sch.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}